/**
 * @fileoverview Gerenciador de autenticação híbrido para mobile e desktop.
 * Detecta dispositivos móveis e usa localStorage para tokens em mobile,
 * mantendo cookies httpOnly para desktop.
 * 
 * @module MobileAuth
 * @since 1.0.0
 */

/**
 * Detecta se está rodando em dispositivo móvel
 * @returns {boolean}
 */
const isMobile = () => /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

/**
 * Gerenciador de autenticação adaptativo
 * @class AuthManager
 */
class AuthManager {
  constructor() {
    this.mobile = isMobile();
    this.initialized = false;
    if (import.meta.env.DEV) {
      console.log('🔧 AuthManager - Mobile detected:', this.mobile);
    }
  }

  /**
   * Inicializa o gerenciador
   */
  init() {
    if (this.initialized) return;
    
    console.log('🔧 [AUTH] Inicializando AuthManager...');
    console.log('📱 [AUTH] Mobile:', this.mobile);
    
    if (this.mobile) {
      const tokens = this.getTokens();
      console.log('🔑 [AUTH] Tokens encontrados:', !!tokens?.access);
    }
    
    this.initialized = true;
  }

  /**
   * Salva tokens (mobile: localStorage | desktop: cookies httpOnly)
   * @param {string} access - Token de acesso
   * @param {string} refresh - Token de refresh
   */
  saveTokens(access, refresh) {
    if (this.mobile) {
      const tokenData = {
        access,
        refresh,
        timestamp: Date.now()
      };
      const encrypted = btoa(JSON.stringify(tokenData));
      localStorage.setItem('vk_auth', encrypted);
      
      if (import.meta.env.DEV) {
        console.log('🔧 Tokens salvos no localStorage (mobile)');
      }
    }
    // Desktop usa cookies httpOnly automaticamente
  }

  /**
   * Recupera tokens salvos (apenas mobile)
   * @returns {Object|null} Dados dos tokens ou null
   */
  getTokens() {
    if (this.mobile) {
      try {
        const data = localStorage.getItem('vk_auth');
        if (data) {
          return JSON.parse(atob(data));
        }
      } catch (error) {
        console.error('Erro ao recuperar tokens:', error);
        this.clearTokens();
      }
    }
    return null;
  }

  /**
   * Remove tokens salvos
   */
  clearTokens() {
    if (this.mobile) {
      localStorage.removeItem('vk_auth');
      if (import.meta.env.DEV) {
        console.log('🔧 Tokens removidos do localStorage (mobile)');
      }
    }
  }

  /**
   * Limpa todos os dados quando sessão expira
   */
  async clearAllData() {
    if (this.mobile) {
      this.clearTokens();
      const { default: dataManager } = await import('./mobileDataManager.js');
      dataManager.clearAll();
    }
  }

  /**
   * Verifica se há tokens válidos
   * @returns {boolean}
   */
  hasValidTokens() {
    if (this.mobile) {
      const tokens = this.getTokens();
      return tokens && tokens.access && tokens.refresh;
    }
    // Desktop: assume que cookies estão presentes se chegou até aqui
    return true;
  }

  /**
   * Valida se a sessão ainda está ativa no servidor
   * @returns {Promise<boolean>}
   */
  async validateSession() {
    if (!this.mobile) return true;
    
    try {
      const { apiRequestJson } = await import('../api/apiService.js');
      const result = await apiRequestJson('auth_session');
      return result.success && result.data.session_active;
    } catch (error) {
      return false;
    }
  }
}

export default new AuthManager();